import { useId, useState, type ReactNode } from 'react';
import { DialogLayer } from '../ui/Dialog';
import { dayMonth, questionLines, type BatchQuestion } from './peopleGrid';
import { missingLines, type MissingDay } from './staffing';

/**
 * Owner 2026-10-27 08:14–08:22 — the grid of „Dostępność → Ludzie” never grows with its text: every tile is a fixed
 * square, every bar spans exactly its days, the name column has one width. Text that does not fit is clipped and
 * scrolls while the pointer is over it (`Clip` + `marqueeOn` / `marqueeOff`); numbers live in a hover bubble.
 */

/** A line of text clipped to its box; it scrolls on hover (see `marqueeOn`). */
export function Clip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={`pg-clip${className ? ` ${className}` : ''}`}>
      <span className="pg-run">{children}</span>
    </span>
  );
}

/** On hover: every clipped line inside that does not fit scrolls to its end and back (only as far as it overflows). */
export function marqueeOn(e: React.SyntheticEvent<HTMLElement>): void {
  for (const clip of e.currentTarget.querySelectorAll<HTMLElement>('.pg-clip')) {
    const run = clip.firstElementChild as HTMLElement | null;
    const over = run ? run.scrollWidth - clip.clientWidth : 0;
    if (over <= 1) continue;
    clip.style.setProperty('--pg-shift', `-${over}px`);
    clip.style.setProperty('--pg-dur', `${Math.max(1.2, over / 35).toFixed(1)}s`);
    clip.classList.add('pg-scrolling');
  }
}
export function marqueeOff(e: React.SyntheticEvent<HTMLElement>): void {
  for (const clip of e.currentTarget.querySelectorAll<HTMLElement>('.pg-clip.pg-scrolling')) clip.classList.remove('pg-scrolling');
}

/**
 * A hover (or focus) bubble with `lines`, one per line — the numbers of a bar („Realizator 1/1, Technik 2/3”), the
 * events of a „Brakuje” day. The text is the wrapped element's description too (`aria-describedby`).
 */
export function Bubble({ lines, children }: { lines: readonly string[]; children: (describedBy: string | undefined) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const has = lines.length > 0;
  return (
    <span
      className="pg-bubble-wrap"
      onMouseEnter={(e) => {
        setOpen(true);
        marqueeOn(e);
      }}
      onMouseLeave={(e) => {
        setOpen(false);
        marqueeOff(e);
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children(has ? id : undefined)}
      {has && (
        <span id={id} hidden>
          {lines.join(', ')}
        </span>
      )}
      {has && open && (
        <span className="pg-tip" role="tooltip">
          {lines.map((l, i) => (
            <span key={i} className="pg-tip-line">
              {l}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * Owner 08:21–08:22: a day of the „Brakuje” row — a red tile with the number of people missing that day in every visible
 * event (no label). The bubble: how many per event, one per line („inne wydarzenie” for one the user may not see). A
 * click: one event — its side panel; several — a small list of the events to open; none the user may see — nothing.
 */
export function MissingTile({ day, open, onOpen, onChoose }: { day: MissingDay; open: boolean; onOpen: (open: boolean) => void; onChoose: (eventId: string) => void }) {
  const named = day.events.filter((e) => e.eventId);
  const click = () => {
    if (day.events.length === 1 && named.length === 1) onChoose(named[0]!.eventId!);
    else if (named.length > 0) onOpen(!open);
  };
  return (
    <>
      <Bubble lines={missingLines(day)}>
        {(describedBy) => (
          <button className="pg-miss" type="button" aria-label={`Brakuje ${dayMonth(day.day)}: ${day.total}`} aria-describedby={describedBy} aria-expanded={day.events.length > 1 && named.length > 0 ? open : undefined} onClick={click}>
            {day.total}
          </button>
        )}
      </Bubble>
      {open && (
        <div className="pg-miss-list" role="group" aria-label={`Brakuje ${dayMonth(day.day)}`}>
          {day.events.map((e, i) =>
            e.eventId ? (
              <button key={e.eventId} type="button" onClick={() => onChoose(e.eventId!)}>
                {e.eventName}: {e.missing}
              </button>
            ) : (
              <span key={`inne-${i}`} className="muted">
                {e.eventName}: {e.missing}
              </span>
            ),
          )}
        </div>
      )}
    </>
  );
}

/**
 * Owner 2026-10-27 08:14: „Niedostępny” where the person is staffed that day — one question for every such person, day
 * and event: „Tak” releases them from those days (the place stays „do obsadzenia”) and marks the days; „Nie” leaves
 * those cells as they are and marks the rest. Nothing is sent to anybody.
 */
export function ReleaseQuestion({ question, nameOf, busy, onAnswer, onClose }: { question: BatchQuestion[]; nameOf: (personId: string) => string; busy: boolean; onAnswer: (release: boolean) => void; onClose: () => void }) {
  const id = useId();
  // review M6: „Tak” only when the user may take someone off; those they may not stay as they are
  const refused = question.filter((q) => q.mayRelease === false);
  const mayAny = refused.length < question.length;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby={id} data-feature-id="crew.people_availability">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id={id}>Wyrzucić z obsady?</h2>
        <p>Wyrzucić z obsady w dniach:</p>
        <ul className="pg-question-list">
          {questionLines(question, nameOf).map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        {mayAny ? (
          <p className="muted">„Tak” — osoba schodzi ze stanowiska (miejsce zostaje do obsadzenia), a dzień dostaje „Niedostępny”. „Nie” — te dni zostają bez zmian, pozostałe dostają „Niedostępny”. Nikt nie dostanie wiadomości.</p>
        ) : (
          <p className="muted">Nie masz prawa usuwać z obsady tych wydarzeń („Obsada: Usuwanie”). „Nie” — te dni zostają bez zmian, pozostałe dostają „Niedostępny”.</p>
        )}
        {mayAny && refused.length > 0 && <p className="muted">Bez prawa do usunięcia z obsady — zostaną bez zmian: {questionLines(refused, nameOf).join('; ')}.</p>}
        <div className="actions">
          {mayAny && (
            <button className="btn primary" type="button" disabled={busy} onClick={() => onAnswer(true)}>
              Tak
            </button>
          )}
          <button className="btn" type="button" disabled={busy} onClick={() => onAnswer(false)}>
            Nie
          </button>
          <button className="btn ghost" type="button" disabled={busy} onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
